import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import usePolling, { keepIfSame } from "@/hooks/usePolling";
import { api } from "@/lib/api";

const POLL_MS = 30_000;
export const STOCK_DISPLAY_DEFAULT = { show_counts: false, available_label: "Available", sold_out_label: "Sold Out", count_label: "{n} pizzas left", alert_label: "Tell me when pizzas are back" };
const BatchContext = createContext({ batch: null, refresh: () => {}, display: STOCK_DISPLAY_DEFAULT });
export const useBatch = () => useContext(BatchContext);

export const pizzaCount = (items, batch) => {
  const ids = new Set(batch?.pizza_item_ids || []);
  return items.reduce((n, l) => n + (ids.has(l.item_id) ? l.qty : 0), 0);
};

export const stockText = (left, display) => (left === 0 ? display.sold_out_label : display.show_counts ? display.count_label.replace("{n}", left) : display.available_label);

export const pizzasLeft = (batch) => {
  if (!batch || batch.status === "completed") return null;
  if (batch.status === "sold_out") return 0;
  return batch.pizzas_left ?? Math.round(batch.remaining_trays * (batch.pizzas_per_tray || 4));
};

// Today's pizza count caps what the batch can sell; a used-up count reads as sold out.
const withDailyCounts = (batch, counts) => {
  const day = counts?.pizza;
  if (!batch || day?.limit == null) return batch;
  const left = Math.min(pizzasLeft(batch) ?? Infinity, day.remaining);
  const soldOut = day.remaining <= 0 && batch.status === "live_drop";
  return { ...batch, pizzas_left: left, remaining_trays: Math.min(batch.remaining_trays, left / (batch.pizzas_per_tray || 4)),
    status: soldOut ? "sold_out" : batch.status, day_sold_out: day.remaining <= 0 };
};

export function BatchProvider({ children }) {
  const [batch, setBatch] = useState(null);
  const [dailyCounts, setDailyCounts] = useState(null);
  const [display, setDisplay] = useState(STOCK_DISPLAY_DEFAULT);
  useEffect(() => { api.get("/stock-display").then(({ data }) => setDisplay(data)).catch(() => {}); }, []);
  const refresh = useCallback(async () => {
    try {
      const [{ data }, counts] = await Promise.all([api.get("/batches/active"), api.get("/daily-counts").catch(() => null)]);
      setBatch(data.active ? keepIfSame(data) : null);
      if (counts) setDailyCounts(keepIfSame(counts.data));
    } catch {
      /* offline or transient — keep last known batch */
    }
  }, []);
  usePolling(refresh, POLL_MS);
  const value = useMemo(() => ({ batch: withDailyCounts(batch, dailyCounts), dailyCounts, refresh, display }), [batch, dailyCounts, refresh, display]);
  return <BatchContext.Provider value={value}>{children}</BatchContext.Provider>;
}
